<template>
  <div class="m-0" v-if="isNumericalMission">
    <div class="text-xs-center" v-if="displayComputationWidget">
      <exo-mission-computation></exo-mission-computation>
    </div>
    <div v-if="displayResult">
      <v-row>
        <v-col class="pb-3">
          <v-row no-gutters>
            <div class="d-inline-flex ml-3 justify-center align-center">
              <span class="headline grey--text"
                >{{ currentMission.name.toUpperCase() }}
                <sup
                  v-if="
                    dashboardTools().isStationKeepingLEOMission(
                      this.currentMission
                    )
                  "
                  class="orange--text"
                  >BETA</sup
                ></span
              >
            </div>
            <v-spacer></v-spacer>
            <v-bottom-sheet inset v-if="displayWarning">
              <template v-slot:activator="{ on: bottomSheet }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      large
                      icon
                      color="warning"
                      v-on="{ ...tooltip, ...bottomSheet }"
                    >
                      <icon
                        scale="1.5"
                        color="warning"
                        class="orange--text"
                        name="exclamation"
                      ></icon>
                    </v-btn>
                  </template>
                  <span>Alert message</span>
                </v-tooltip>
              </template>
              <v-card>
                <div class="alert-div"></div>
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="displayWarningEccentricity"
                >
                  {{ warningEccentricityMessage }}
                </v-alert>
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="displayWarningDutyCycle"
                >
                  Disclaimer: the spacecraft orbital average power was not
                  enough to supply the propulsion system warm-up and thrust. To
                  respect the max mean duty cycle, the orbital duty cycle used
                  in the simulation was lower than the one selected by the user:
                  <br />
                  <i
                    >spacecraft definition duty cycle:
                    {{
                      (
                        100 *
                        this.currentResultsLog.results.maneuveringStrategy
                          .spacecraftDefinitionDutyCycle
                      ).toFixed(2)
                    }}
                    % / orbital duty cycle used in the simulation:
                    {{
                      (
                        100 *
                        this.currentResultsLog.results.maneuveringStrategy
                          .orbitalDutyCycle
                      ).toFixed(2)
                    }}
                    %</i
                  >
                </v-alert>
              </v-card>
            </v-bottom-sheet>
            <v-tooltip bottom v-if="ephemeridesAvailable">
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="addPlotConfiguration()"
                >
                  <icon
                    scale="1.5"
                    class="primary--text"
                    name="regular/chart-bar"
                  ></icon>
                </v-btn>
              </template>
              <span>Add plot</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="dashboardTools().saveLayout(_self)"
                >
                  <icon scale="1.5" class="primary--text" name="save"></icon>
                </v-btn>
              </template>
              <span
                >Save the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="dashboardTools().resetLayout(_self)"
                >
                  <icon
                    scale="1.5"
                    class="primary--text"
                    name="undo-alt"
                  ></icon>
                </v-btn>
              </template>
              <span
                >Reset the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
            <v-dialog width="600" v-model="finalOrbitDialog">
              <template v-slot:activator="{ on: dialog }">
                <v-tooltip bottom v-if="isFinalOrbit">
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      large
                      icon
                      color="primary"
                      v-on="{ ...tooltip, ...dialog }"
                    >
                      <icon scale="1.5" color="primary" name="globe"></icon>
                    </v-btn>
                  </template>
                  <span>Save final Orbit</span>
                </v-tooltip>
              </template>
              <v-card>
                <v-card-title>
                  Save final Orbit
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    small
                    class="m-0"
                    dark
                    @click="finalOrbitDialog = false"
                  >
                    <v-icon>close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-text-field
                  :label="'Name*'"
                  v-model="finalOrbitName"
                  maxlength="100"
                  class="ma-6"
                  :rules="[this.inputNameFeedback]"
                ></v-text-field>
                <v-btn
                  :disabled="isEmptyFinalOrbitName"
                  color="primary"
                  class="ma-6"
                  v-on:click="saveFinalOrbit()"
                  >Save final Orbit</v-btn
                >
              </v-card>
            </v-dialog>
          </v-row>
        </v-col>
      </v-row>
      <v-divider class="mt-0"></v-divider>
      <div class="px-3">
        <numerical-plot-config
          ref="plotConfig"
          v-if="currentResultsLog.results.ephemerides != null"
        ></numerical-plot-config>
        <grid-layout
          :layout.sync="gridlayout"
          :col-num="6"
          :row-height="7"
          :is-draggable="true"
          :is-resizable="true"
          :is-mirrored="false"
          :vertical-compact="true"
          :margin="[20, 10]"
          :use-css-transforms="true"
        >
          <grid-item
            :x="gridlayout[0].x"
            :y="gridlayout[0].y"
            :w="gridlayout[0].w"
            :h="gridlayout[0].h"
            :i="gridlayout[0].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              v-if="
                !dashboardTools().isOrbitExtrapolationMission(currentMission) &&
                !dashboardTools().isManeuveringStrategyDesignMission(
                  currentMission
                )
              "
              color="#2A94DE"
              awesomeicon="tachometer-alt"
              title="ΔV"
              :ref="`${statsCardName}${gridlayout[0].i}`"
              :cardId="`${statsCardName}${gridlayout[0].i}`"
              :value="computeResultToDisplay(['maneuverReport', 'totalDeltaV'])"
              :unit="getResultUnitLabel(['maneuverReport', 'totalDeltaV'])"
            />
            <stats-card
              v-if="
                dashboardTools().isOrbitExtrapolationMission(currentMission)
              "
              color="#2A94DE"
              awesomeicon="mountain"
              title="Semi-major axis change"
              :ref="`${statsCardName}${gridlayout[0].i}`"
              :cardId="`${statsCardName}${gridlayout[0].i}`"
              :value="computeResultToDisplay(['deltaSemiMajorAxis'])"
              :unit="getResultUnitLabel(['deltaSemiMajorAxis'])"
            />
            <stats-card
              v-if="
                dashboardTools().isManeuveringStrategyDesignMission(
                  currentMission
                )
              "
              color="#2A94DE"
              awesomeicon="regular/dot-circle"
              :title="
                computeResultToDisplay(['thrustModeLabel']) + ' mean power'
              "
              :ref="`${statsCardName}${gridlayout[0].i}`"
              :cardId="`${statsCardName}${gridlayout[0].i}`"
              :value="computeResultToDisplay(['thrustModeMeanPower'])"
              :unit="getResultUnitLabel(['thrustModeMeanPower'])"
            />
          </grid-item>
          <grid-item
            :x="gridlayout[1].x"
            :y="gridlayout[1].y"
            :w="gridlayout[1].w"
            :h="gridlayout[1].h"
            :i="gridlayout[1].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              v-if="
                !dashboardTools().isOrbitExtrapolationMission(currentMission) &&
                !dashboardTools().isManeuveringStrategyDesignMission(
                  currentMission
                )
              "
              color="#FF7E0D"
              icon="ev_station"
              title="Used propellant"
              :ref="`${statsCardName}${gridlayout[1].i}`"
              :cardId="`${statsCardName}${gridlayout[1].i}`"
              :value="
                computeResultToDisplay(['maneuverReport', 'totalConsumption'])
              "
              :unit="getResultUnitLabel(['maneuverReport', 'totalConsumption'])"
            />
            <!-- balance-scale-right -->
            <stats-card
              v-if="
                dashboardTools().isOrbitExtrapolationMission(currentMission)
              "
              color="#FF7E0D"
              awesomeicon="snowboarding"
              title="Inclination change"
              :ref="`${statsCardName}${gridlayout[1].i}`"
              :cardId="`${statsCardName}${gridlayout[1].i}`"
              :value="computeResultToDisplay(['deltaInclination'])"
              :unit="getResultUnitLabel(['deltaInclination'])"
            />
            <stats-card
              v-if="
                dashboardTools().isManeuveringStrategyDesignMission(
                  currentMission
                )
              "
              color="#FF7E0D"
              awesomeicon="satellite-dish"
              :title="computeResultToDisplay(['restModeLabel']) + ' mean power'"
              :ref="`${statsCardName}${gridlayout[1].i}`"
              :cardId="`${statsCardName}${gridlayout[1].i}`"
              :value="computeResultToDisplay(['restModeMeanPower'])"
              :unit="getResultUnitLabel(['restModeMeanPower'])"
            />
          </grid-item>
          <grid-item
            :x="gridlayout[2].x"
            :y="gridlayout[2].y"
            :w="gridlayout[2].w"
            :h="gridlayout[2].h"
            :i="gridlayout[2].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              v-if="
                !dashboardTools().isOrbitExtrapolationMission(currentMission) &&
                !dashboardTools().isManeuveringStrategyDesignMission(
                  currentMission
                )
              "
              color="#F72C2C"
              icon="timelapse"
              title="Thrust duration"
              :ref="`${statsCardName}${gridlayout[2].i}`"
              :cardId="`${statsCardName}${gridlayout[2].i}`"
              :value="
                computeResultToDisplay(['maneuverReport', 'totalBurnsDuration'])
              "
              :unit="
                getResultUnitLabel(['maneuverReport', 'totalBurnsDuration'])
              "
            />
            <stats-card
              v-if="
                dashboardTools().isOrbitExtrapolationMission(currentMission)
              "
              color="#F72C2C"
              awesomeicon="history"
              title="RAAN change"
              :ref="`${statsCardName}${gridlayout[2].i}`"
              :cardId="`${statsCardName}${gridlayout[2].i}`"
              :value="computeResultToDisplay(['deltaRaan'])"
              :unit="getResultUnitLabel(['deltaRaan'])"
            />
            <stats-card
              v-if="
                dashboardTools().isManeuveringStrategyDesignMission(
                  currentMission
                )
              "
              color="#F72C2C"
              awesomeicon="battery-quarter"
              title="Battery's depth of discharge"
              :ref="`${statsCardName}${gridlayout[2].i}`"
              :cardId="`${statsCardName}${gridlayout[2].i}`"
              :value="
                computeResultToDisplay([
                  'spacecraft',
                  'batteryDefinition',
                  'depthOfDischarge'
                ])
              "
              :unit="
                getResultUnitLabel([
                  'spacecraft',
                  'batteryDefinition',
                  'depthOfDischarge'
                ])
              "
            />
          </grid-item>
          <grid-item
            :x="gridlayout[3].x"
            :y="gridlayout[3].y"
            :w="gridlayout[3].w"
            :h="gridlayout[3].h"
            :i="gridlayout[3].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <stats-card
              v-if="
                !dashboardTools().isManeuveringStrategyDesignMission(
                  currentMission
                )
              "
              color="#1d2939"
              icon="timer"
              title="Mission duration"
              :ref="`${statsCardName}${gridlayout[3].i}`"
              :cardId="`${statsCardName}${gridlayout[3].i}`"
              :value="
                computeResultToDisplay(['maneuverReport', 'simulationDuration'])
              "
              :unit="
                getResultUnitLabel(['maneuverReport', 'simulationDuration'])
              "
            />
            <stats-card
              v-if="
                dashboardTools().isManeuveringStrategyDesignMission(
                  currentMission
                )
              "
              color="#1d2939"
              awesomeicon="battery-full"
              title="Battery's nominal capacity"
              :ref="`${statsCardName}${gridlayout[3].i}`"
              :cardId="`${statsCardName}${gridlayout[3].i}`"
              :value="
                computeResultToDisplay([
                  'spacecraft',
                  'batteryDefinition',
                  'nominalCapacity'
                ])
              "
              :unit="
                getResultUnitLabel([
                  'spacecraft',
                  'batteryDefinition',
                  'nominalCapacity'
                ])
              "
            />
          </grid-item>
          <grid-item
            :x="gridlayout[4].x"
            :y="gridlayout[4].y"
            :w="gridlayout[4].w"
            :h="
              !dashboardTools().isSimulatorMission(this.currentMission)
                ? gridlayout[4].h
                : 6
            "
            :i="gridlayout[4].i"
            :minH="5"
            :minW="1"
            @resized="statsCardResized"
            @resize="statsCardResized"
          >
            <v-col
              v-if="!dashboardTools().isSimulatorMission(this.currentMission)"
              fill-height
              d-flex
            >
              <results-card :root="_self">
                <v-btn
                  text
                  outlined
                  color="green"
                  v-if="isEphemeridesFilesGenerated"
                  v-on:click="exportEphemeridesFiles()"
                >
                  DOWNLOAD FILES
                </v-btn>
              </results-card>
            </v-col>
            <stats-card
              v-if="
                dashboardTools().isSimulatorMission(currentMission) &&
                resultsLoaded() &&
                this.currentResultsLog.results.measures
              "
              color="#B264C5"
              icon="file_download_done"
              title="Nb of measures"
              :ref="`${statsCardName}${gridlayout[4].i}`"
              :cardId="`${statsCardName}${gridlayout[4].i}`"
              :value="
                this.currentResultsLog.results.measures[0].measurements.length
              "
              :unit="' '"
            />
          </grid-item>

          <div v-for="(config, index) in configurations" :key="config.id">
            <grid-item
              v-if="config.gridItem"
              :x="config.gridItem.x"
              :y="config.gridItem.y"
              :w="config.gridItem.w"
              :h="config.gridItem.h"
              :i="config.gridItem.i"
              :isDraggable="config.isDraggable"
              @resize="plotCardResized"
              @resized="plotCardResized"
            >
              <v-card tile height="100%">
                <v-row>
                  <v-col cols="12">
                    <v-btn
                      color="primary"
                      @click.stop="
                        dashboardTools().editPlotConfiguration(_self, index)
                      "
                      left
                      dark
                      text
                    >
                      <v-icon dark medium>edit</v-icon>
                      Edit
                    </v-btn>
                    <v-btn
                      color="primary"
                      @click.stop="
                        dashboardTools().removePlotConfiguration(
                          _self,
                          config.gridItem.i
                        )
                      "
                      left
                      dark
                      text
                    >
                      <v-icon dark medium>delete</v-icon>
                      Delete
                    </v-btn>
                  </v-col>
                </v-row>
                <plot-item
                  :gridlayout="gridlayout"
                  height="100%"
                  :root="_self"
                  :config="config"
                  :id="`${plotingAreaName}${config.gridItem.i}`"
                  :ref="`${plotingAreaName}${config.gridItem.i}`"
                  class="elevation-5"
                >
                </plot-item>
              </v-card>
            </grid-item>
          </div>
        </grid-layout>
        <div
          v-if="
            dashboardTools().isSimulatorMission(currentMission) &&
            this.currentResultsLog
          "
        >
          <v-col v-if="this.currentResultsLog.results.measures">
            <!--v-row>
              [MEASUREMENT_DATE, RANGE, RANGE_RATE, AZIMUTH, ELEVATION]
            </v-row-->
            <v-row>
              {{ this.currentResultsLog.results.measures[0].measurements[0] }}
            </v-row>
            <v-row>
              {{ this.currentResultsLog.results.measures[0].measurements[1] }}
            </v-row>
            <v-row>
              {{ this.currentResultsLog.results.measures[0].measurements[2] }}
            </v-row>
            <v-row>
              {{ this.currentResultsLog.results.measures[0].measurements[3] }}
            </v-row>
            <v-row>
              {{ this.currentResultsLog.results.measures[0].measurements[4] }}
            </v-row>
            <v-row>
              {{ this.currentResultsLog.results.measures[0].measurements[5] }}
            </v-row>
            <v-row>
              ...
              {{
                this.currentResultsLog.results.measures[0].measurements.length -
                7
              }}
              more ...
            </v-row>
            <v-row>
              {{
                this.currentResultsLog.results.measures[0].measurements[
                  this.currentResultsLog.results.measures[0].measurements
                    .length - 1
                ]
              }}
            </v-row>
          </v-col>
        </div>
        <div
          v-if="
            dashboardTools().isSimulatorMission(currentMission) &&
            resultsLoaded() &&
            this.currentResultsLog.results.measures
          "
        >
          <v-btn
            small
            :disabled="dataExtracted"
            v-on:click="extractMeasurements()"
            color="primary"
            dark
            >Extract Data</v-btn
          >
        </div>
        <div
          v-if="
            dataExtracted &&
            dashboardTools().isSimulatorMission(currentMission) &&
            this.currentResultsLog.results.measures
          "
        >
          <v-col>
            <v-row>
              {{ 'Data successfully extracted to database!' }}
            </v-row>
          </v-col>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dashboardComponents from '../../components/Dashboard/index.js'
import dataTools from '../../tools/data.js'
import dashboardTools from '../../tools/dashboard.js'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import plotInput from '../../mixins/plotInput.js'
import plotLayout from '../../mixins/plotLayout.js'
import plotData from '../../mixins/plotData.js'
import Plotly from 'plotly.js-dist'
import { setTimeout } from 'timers'

Vue.use(dashboardComponents)

export default {
  name: 'NumericalDashboard',
  mixins: [errorMixin, plotLayout, plotInput, plotData],

  data() {
    return {
      dataExtracted: false,
      finalOrbitDialog: false,
      finalOrbitName: '',
      configurations: [],
      isDrawn: [],
      drawer: false,
      plotingAreaName: 'plotingAreaNumerical',
      statsCardName: 'statsCard',
      type: 'SimulationResult',
      configuration: this.buildEmptyConfiguration(),
      defaultLayout: [
        { x: 0, y: 0, w: 1, h: 6, i: '0' },
        { x: 1, y: 0, w: 1, h: 6, i: '1' },
        { x: 2, y: 0, w: 1, h: 6, i: '2' },
        { x: 3, y: 0, w: 1, h: 6, i: '3' },
        { x: 4, y: 0, w: 1, h: 22, i: '4' }
      ],
      gridlayout: [
        { x: 0, y: 0, w: 1, h: 6, i: '0' },
        { x: 1, y: 0, w: 1, h: 6, i: '1' },
        { x: 2, y: 0, w: 1, h: 6, i: '2' },
        { x: 3, y: 0, w: 1, h: 6, i: '3' },
        { x: 4, y: 0, w: 1, h: 22, i: '4' }
      ]
    }
  },

  created() {
    window.addEventListener('beforeunload', this.saveLayout)
  },

  mounted() {
    this.$root.$on('current-results-updated', this.currentResultsUpdatedHandler)
    setTimeout(() => {
      this.loadLayout()
    }, 1)
    this.$root.$on('create-plot', this.createPlotHandler)
  },

  beforeDestroy() {
    this.$root.$off(
      'current-results-updated',
      this.currentResultsUpdatedHandler
    )
    this.$root.$off('create-plot', this.createPlotHandler)
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission',
      currentResultsLog: 'currentResultsLog'
    }),
    displayComputationWidget() {
      return dashboardTools.displayComputationWidget(this)
    },
    displayResult() {
      return dashboardTools.displayResult(this)
    },
    resultLoaded() {
      return dashboardTools.resultLoaded(this)
    },
    isNumericalMission() {
      return (
        this.currentMission != null &&
        this.currentMission.typeName === 'SimulationRequest'
      )
    },
    resultAvailable() {
      return (
        this.isNumericalMission &&
        this.resultLoaded &&
        this.currentResultsLog.results ===
          Object(this.currentResultsLog.results)
      ) // check object, not array
    },
    ephemeridesAvailable() {
      return (
        this.resultAvailable &&
        this.currentResultsLog.results.ephemerides != null
      )
    },
    ephemeridesFilesAvailable() {
      return (
        this.resultAvailable &&
        this.currentResultsLog.results.ephemeridesFiles != null
      )
    },
    displayWarningEccentricity() {
      return dashboardTools.displayWarningEccentricity(this)
    },
    warningEccentricityMessage() {
      return dashboardTools.warningEccentricityMessage(this)
    },
    displayWarningDutyCycle() {
      let maneuveringStrategyResults =
        this.currentResultsLog.results.maneuveringStrategy
      if (
        maneuveringStrategyResults == null ||
        this.currentResultsLog.results.spacecraft.thruster
          .propulsionSystemType === 'CHEMICAL'
      ) {
        return false
      } else {
        return (
          maneuveringStrategyResults.spacecraftDefinitionDutyCycle >
          maneuveringStrategyResults.orbitalDutyCycle
        )
      }
    },
    displayWarning() {
      return this.displayWarningEccentricity || this.displayWarningDutyCycle
    },
    isEphemeridesFilesGenerated() {
      if (this.ephemeridesFilesAvailable) {
        return (
          Object.keys(this.currentResultsLog.results.ephemeridesFiles).length >
          0
        )
      }
      return false
    },
    isFinalOrbit() {
      return (
        this.currentResultsLog?.results?.finalOrbit != null &&
        this.currentResultsLog?.results?.finalOrbit !== ''
      )
    },
    isEmptyFinalOrbitName() {
      if (this.finalOrbitName.length > 0) {
        return false
      }
      return true
    },
    inputNameFeedback() {
      let feedback = true
      if (this.inputNameState !== null && !this.inputNameState) {
        feedback =
          "Please only use the allowed characters : a-z A-Z 0-9 ' ',.-+=/_#@$&:%|()]["
      }
      return feedback
    },
    inputNameState() {
      let valid = null
      if (this.finalOrbitName !== '') {
        let allowedChars = RegExp(
          '^[a-zA-Z0-9 ,\\.\\-\\+=\\/_@#\\$&:%\\|\\(\\)\\[\\]]+$'
        )
        valid = allowedChars.test(this.finalOrbitName)
      }
      return valid
    }
  },

  methods: {
    currentResultsUpdatedHandler() {
      setTimeout(() => {
        this.loadLayout()
        dashboardTools.removeDuplicatedGridItem(this)
      }, 1)
    },
    createPlotHandler(config, plotingArea, fromLayoutSave) {
      if (!fromLayoutSave && config.layout) {
        config.gridItem.h = 25
      }
      // Use timeout as workaround because nextTick doesnt work as expected
      setTimeout(() => {
        let el = this.$refs[`${this.plotingAreaName}${config.gridItem.i}`]
        if (el) {
          el = el[0]
          let parent = el ? el.$parent : undefined
          if (parent) {
            config.layout.height = parent.$el.clientHeight - 50
            if (config.data.length > 0) {
              if (
                config.data[0].x
                  .filter((data) => !isNaN(data) || typeof data === 'string')
                  .some(isNaN)
              ) {
                config.layout.xaxis.categoryorder = 'array'
                config.layout.xaxis.categoryarray = [...config.data[0].x] // to create a new array
                config.layout.xaxis.categoryarray.sort()
              }
              if (
                config.data[0].y
                  .filter((data) => !isNaN(data) || typeof data === 'string')
                  .some(isNaN)
              ) {
                config.layout.yaxis.categoryorder = 'array'
                config.layout.yaxis.categoryarray = [...config.data[0].y] // to create a new array
                config.layout.yaxis.categoryarray.sort()
              }
              Plotly.react(plotingArea, config.data, config.layout, {
                responsive: true
              })
              plotingArea.addEventListener('mouseover', (data) => {
                config.isDraggable = false
              })
              plotingArea.addEventListener('mouseleave', (data) => {
                config.isDraggable = true
              })
            }
          }
        }
      }, 100)
    },
    async extractMeasurements() {
      this.dataExtracted = true
      let measures = this.currentResultsLog.results.measures
      let data = await this.$http
        .post('/users/' + this.currentUser.id + '/' + 'measurements/extract', {
          measures
        })
        .then(function (response) {
          return response.data
        })
        .catch((e, __) => {
          this.errorTrace = this.getErrorMessage(e)
          return null
        })
      return data
    },

    async saveFinalOrbit() {
      let orbit = this.currentResultsLog.results.finalOrbit
      orbit.name = this.finalOrbitName
      this.$http
        .put(
          dashboardTools.getCurrentAPI(this) + '/convertandsavefinalorbit',
          orbit
        )
        .then(() => {
          this.finalOrbitDialog = false
          this.displayMessage(
            'Success: the final Orbit was successfully saved',
            '',
            'notification',
            'success'
          )
        })
        .catch((e) => {
          this.displayMessage(
            'Error: fail to save the final Orbit',
            e.message,
            'notification',
            'error',
            this
          )
        })
    },

    dashboardTools() {
      return dashboardTools
    },

    resultsLoaded() {
      return this.resultLoaded
    },

    addDefaultAxis(axisList, condition, name) {
      if (condition) {
        let parameter = this.buidDefaultPlotParameter()
        parameter.value = name
        this.treeUpdated(parameter, 'parameter')
        axisList.push(parameter)
      }
    },

    loadLayout() {
      if (this.currentMission && this.gridlayout) {
        if (this.currentMission.layout) {
          let missionLayout = JSON.parse(this.currentMission.layout)
          dataTools.loadGridLayoutConfig(missionLayout, this)
        } else {
          let missionLayout = { configurations: [], layout: this.defaultLayout }
          dataTools.loadGridLayoutConfig(missionLayout, this)
          if (
            !this.currentResultsLog ||
            !this.currentResultsLog.results ||
            !this.currentResultsLog.results.fieldIndexes ||
            !this.currentResultsLog.results.ephemerides
          ) {
            return
          }
          /*
          let defaultExists = this.gridlayout.find(el => {
            return el.i === 'plot0'
          }) */
          let defaultExists = false
          if (!defaultExists) {
            let config
            if (
              dashboardTools.isManeuveringStrategyDesignMission(
                this.currentMission
              )
            ) {
              this.configurations = []
              config = this.buildEmptyConfiguration(0)
              config.type = 'Multi axis'
              config.name = ''
              let collectedPowerExported =
                this.currentResultsLog.results.fieldIndexes[
                  'batteryContributionCharge'
                ]
              let attitudeModeExported =
                this.currentResultsLog.results.fieldIndexes['attitudeMode']
              if (!collectedPowerExported && !attitudeModeExported) {
                return
              }
              config.multiAxis = []
              if (collectedPowerExported) {
                config.name = 'Solar array collected power'
                this.addDefaultAxis(
                  config.multiAxis,
                  collectedPowerExported,
                  'battery.ContributionCharge'
                )
              }
              if (attitudeModeExported) {
                config.name =
                  config.name === ''
                    ? 'Attitude mode'
                    : config.name + ' / Attitude mode'
                this.addDefaultAxis(
                  config.multiAxis,
                  attitudeModeExported,
                  'attitude.Mode'
                )
              }
            } else {
              this.configurations = []
              config = this.buildEmptyConfiguration(0)
              config.name = 'Keplerian orbital parameters'
              config.type = 'Multi axis'
              config.multiAxis = []
              let meanExported =
                this.currentResultsLog.results.fieldIndexes['keplerianMeanEcc']
              let oscExported =
                this.currentResultsLog.results.fieldIndexes['keplerianEcc']
              let isSmaMan = false
              let isIncMan = false
              let isEccMan = false
              if (dashboardTools.isRAANMission(this.currentMission)) {
                this.addDefaultAxis(
                  config.multiAxis,
                  dashboardTools.isRAANMission(this.currentMission),
                  'relativeRaan'
                )
                isSmaMan = true
                isIncMan = true
                isEccMan = false
              } else if (
                dashboardTools.isArgumentOfLatitudePhasingMission(
                  this.currentMission
                )
              ) {
                this.addDefaultAxis(
                  config.multiAxis,
                  dashboardTools.isArgumentOfLatitudePhasingMission(
                    this.currentMission
                  ),
                  'relativeArgumentOfLatitude'
                )
                isSmaMan = true
                isIncMan = false
                isEccMan = false
              } else if (
                dashboardTools.isOrbitExtrapolationMission(this.currentMission)
              ) {
                isSmaMan = true
                isIncMan = true
                isEccMan = true
              } else if (
                dashboardTools.isStationKeepingLEOMission(this.currentMission)
              ) {
                this.addDefaultAxis(
                  config.multiAxis,
                  dashboardTools.isStationKeepingLEOMission(
                    this.currentMission
                  ),
                  'relativeArgumentOfLatitude'
                )
                isSmaMan = true
                isIncMan = false
                isEccMan = false
                this.drawVelocityRadialposError()
              } else {
                isSmaMan =
                  this.currentMission.deltaSemiMajorAxis != null &&
                  this.currentMission.deltaSemiMajorAxis !== 0
                isIncMan =
                  this.currentMission.deltaInclination != null &&
                  this.currentMission.deltaInclination !== 0
                isEccMan =
                  this.currentMission.deltaEccentricity != null &&
                  this.currentMission.deltaEccentricity !== 0
              }

              if (meanExported) {
                this.addDefaultAxis(
                  config.multiAxis,
                  isSmaMan,
                  'keplerian.MeanSma'
                )
                this.addDefaultAxis(
                  config.multiAxis,
                  isIncMan,
                  'keplerian.MeanInc'
                )
                this.addDefaultAxis(
                  config.multiAxis,
                  isEccMan,
                  'keplerian.MeanEcc'
                )
              } else if (oscExported) {
                this.addDefaultAxis(config.multiAxis, isSmaMan, 'keplerian.Sma')
                this.addDefaultAxis(config.multiAxis, isIncMan, 'keplerian.Inc')
                this.addDefaultAxis(config.multiAxis, isEccMan, 'keplerian.Ecc')
              } else {
                return
              }
            }
            let gridItem = {
              x: 0,
              y: 1,
              w: 4,
              h: 25,
              i: `plot0`
            }
            config.id = 0
            config.gridItem = gridItem
            this.configuration = config
            this.configurations.push(config)
            this.gridlayout.push(this.configuration.gridItem)
            this.$nextTick(() => {
              let plotingArea = document.getElementById(
                `${this.plotingAreaName}${this.configuration.gridItem.i}`
              )
              if (plotingArea != null) {
                this.$root.$emit(
                  'set-plot-config',
                  this.configuration,
                  plotingArea
                )
              }
              this.basicPlotLoadWorkaround()
            })
          }
        }
      }
    },
    drawVelocityRadialposError() {
      let gridItem = {
        x: 0,
        y: 1,
        w: 4,
        h: 25,
        i: `plot1`
      }
      let config = this.buildEmptyConfiguration(0)
      config.type = 'Line/Scatter chart'
      config.scatterMode = 'markers'
      // config.mode = 'Markers'
      // config.type.scatterModesLineChart = 'markers'
      // config.DisplayMode = 'Markers'
      config.name =
        'Position error of satellite with respect of natural trajectory'
      config.x.value = 'position.ErrorInVelocityDirection'
      this.treeUpdated(config.x, 'x')
      config.y.value = 'position.ErrorInRadialDirection'
      this.treeUpdated(config.y, 'y')
      config.id = 1
      config.gridItem = gridItem
      this.configuration = config
      this.configurations.push(config)
      this.gridlayout.push(this.configuration.gridItem)
      this.$nextTick(() => {
        let plotingArea = document.getElementById(
          `${this.plotingAreaName}${this.configuration.gridItem.i}`
        )
        if (plotingArea != null) {
          this.$root.$emit('set-plot-config', this.configuration, plotingArea)
        }
        this.basicPlotLoadWorkaround()
      })
    },

    statsCardResized(i) {
      dashboardTools.statsCardResized(this, i)
    },

    // basicPlotLoadWorkaround is a function to handle the fact that plots may spawn at the bottom of the page
    // Unfortunately, we can't resolve it the proper way until we make some cleaning in the event bus ¯\_( ͡° ͜ʖ ͡°)_/¯
    basicPlotLoadWorkaround() {
      let grid = this.gridlayout
      let newGrid = []
      grid.forEach((el) => {
        let exists = newGrid.find((elB) => {
          return elB.i === el.i
        })
        if (!exists) {
          newGrid.push(el)
        }
      })
      this.gridlayout = newGrid
      let plot0Conf = this.configurations.find(
        (el) => el.gridItem.i === 'plot0'
      )
      if (plot0Conf != null) {
        plot0Conf.gridItem = this.gridlayout.find((el) => el.i === 'plot0')
      }
    },

    plotCardResized(i, newH, newW, newHPx, newWPx) {
      let update = {
        width: newWPx,
        height: newHPx - 50
      }
      let plotingArea = document.getElementById(`${this.plotingAreaName}${i}`)
      if (plotingArea != null) {
        try {
          Plotly.relayout(plotingArea, update)
        } catch (e) {
          console.log('Error calling Plotly.relayout')
        }
      }
    },

    addPlotConfiguration() {
      dashboardTools.removeDuplicatedGridItem(this)

      let lastId =
        this.configurations.length > 0
          ? this.configurations.reduce((acc, curr) => {
              return curr.id > acc.id ? curr : acc
            }).id
          : 0
      let newId = lastId + 1
      let newConfig = this.buildEmptyConfiguration(newId)
      this.configuration = newConfig
      let biggestY = this.gridlayout.reduce((acc, curr) => {
        return acc.y > curr.y ? acc : curr
      }, this.gridlayout[0])
      let gridItem = {
        x: 0,
        y: biggestY.y + biggestY.h,
        w: 4,
        h: 25,
        i: `${'plot' + newId}`
      }
      this.configuration.gridItem = gridItem
      this.configurations.push(this.configuration)
      this.$nextTick(() => {
        this.gridlayout.push(this.configuration.gridItem)
        this.$nextTick(() => {
          let plotingArea = document.getElementById(
            `${this.plotingAreaName}${this.configuration.gridItem.i}`
          )
          if (plotingArea != null) {
            this.$root.$emit('set-plot-config', this.configuration, plotingArea)
          }
          this.$refs.plotConfig.drawer = !this.$refs.plotConfig.drawer
        })
      })
    },

    getResultUnitLabel(fieldPath) {
      return dashboardTools.getResultUnitLabel(this, fieldPath)
    },

    buildPath(path) {
      if (!this.resultAvailable) {
        return
      }
      let field = this.currentResultsLog.results
      for (let i = 0; i < path.length; i++) {
        field = field[path[i]]
      }
      return field
    },

    computeResultToDisplay(fieldPath) {
      let fieldType = dataTools.getFieldTypeFromPath(fieldPath, this.type, null)
      if (fieldPath.includes('ephemerides')) {
        // range shown
        return '???'
      } else {
        return dataTools
          .convertSi2Display(fieldType, this.buildPath(fieldPath))
          .toString()
      }
    },

    computeRange(fieldType, fieldPath) {
      if (!this.resultAvailable) {
        return ''
      }
      let data = dataTools.convertSimulationData(
        this.currentResultsLog.results.ephemerides,
        this.currentResultsLog.results.fieldIndexes
      )
      let allVals = dataTools
        .getUniqueValues(data, fieldPath)
        .filter((value) => !isNaN(value))
      let min = dataTools.convertSi2Display(fieldType, Math.min(...allVals))
      let max = dataTools.convertSi2Display(fieldType, Math.max(...allVals))
      if (min === max) {
        return min.toString()
      } else {
        if (min.toString().includes('Infinity')) {
          return ''
        }
        let displayedString = '[' + min
        allVals.length === 2
          ? (displayedString += '; ')
          : (displayedString += ' ... ')
        return displayedString + max + ']'
      }
    },

    mergeString(fieldPath) {
      if (!this.resultAvailable) {
        return
      }
      let data = dataTools.convertSimulationData(
        this.currentResultsLog.results.ephemerides,
        this.currentResultsLog.results.fieldIndexes
      )
      let uniqueVals =
        dataTools.getUniqueValues(data, fieldPath) ||
        dataTools.getUniqueValues(this.currentResultsLog.results, fieldPath)
      if (uniqueVals.length === 1) {
        return uniqueVals[0]
      } else {
        return uniqueVals
      }
    },

    isStringInside(expected, fieldPath) {
      if (!this.resultAvailable) {
        return
      }
      let data = dataTools.convertSimulationData(
        this.currentResultsLog.results.ephemerides,
        this.currentResultsLog.results.fieldIndexes
      )
      return (
        data.filter(
          (element) => dataTools.getValue(element, fieldPath) === expected
        ).length > 0
      ) // TODO MFI stop on first would be better
    },

    exportEphemeridesFiles() {
      let files = this.currentResultsLog.results.ephemeridesFiles
      console.log(files)
      Object.entries(files).forEach((entry) => {
        let key = entry[0]
        let value = entry[1]
        const filename = this.currentMission.name + '_' + key
        dataTools.downloadFile(filename, value.join(''), 'txt', false) // no utf8
      })
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');
</style>
